import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Plus, X, Save, Loader2, Trash2, Edit2, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GeofenceSetup = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [fences, setFences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    location_name: '', address: '', latitude: '', longitude: '',
    radius_meters: '200', enforce_for_all: true,
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchFences = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/attendance/geofences?company_id=${selectedCompany}`, { withCredentials: true });
      setFences(data);
    } catch (e) { console.error(e); setFences([]); }
    finally { setLoading(false); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchFences(); }, [selectedCompany, fetchFences]);



  const handleSave = async () => {
    if (!form.location_name || !form.latitude || !form.longitude) {
      toast.error('Location name, latitude and longitude are required'); return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/attendance/geofences/${editingId}`, {
          ...form, company_id: selectedCompany,
        }, { withCredentials: true });
        toast.success('Geofence updated');
      } else {
        await axios.post(`${API_URL}/attendance/geofences`, {
          ...form, company_id: selectedCompany,
        }, { withCredentials: true });
        toast.success('Geofence created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ location_name: '', address: '', latitude: '', longitude: '', radius_meters: '200', enforce_for_all: true });
      fetchFences();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (fence) => {
    setEditingId(fence.id);
    setForm({
      location_name: fence.location_name || '',
      address: fence.address || '',
      latitude: String(fence.latitude || ''),
      longitude: String(fence.longitude || ''),
      radius_meters: String(fence.radius_meters || 200),
      enforce_for_all: fence.enforce_for_all !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this geofence?')) return;
    try {
      await axios.delete(`${API_URL}/attendance/geofences/${id}`, { withCredentials: true });
      toast.success('Geofence deleted');
      fetchFences();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="geofence-setup-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <MapPin className="inline-block w-8 h-8 mr-2 mb-1" /> Geo-fence Setup
            </h1>
            <p className="mt-1 text-sm text-slate-600">Define office locations and attendance boundaries</p>
          </div>
          <div className="flex gap-3">
            <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="geofence-company-select"
              className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button data-testid="add-geofence-btn" onClick={() => { setEditingId(null); setForm({ location_name: '', address: '', latitude: '', longitude: '', radius_meters: '200', enforce_for_all: true }); setShowForm(true); }}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Location
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
          <strong>How Geo-fencing Works:</strong> Define office locations with coordinates and radius. When employees mark attendance via mobile,
          the system validates their GPS position falls within the defined boundary. Attendance outside the fence is flagged as an exception.
        </div>

        {/* Geofences Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : fences.length === 0 ? (
            <div className="col-span-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-12 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No geofences defined yet</p>
              <p className="text-xs text-slate-400 mt-1">Click "Add Location" to define your first office boundary</p>
            </div>
          ) : fences.map(fence => (
            <div key={fence.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm transition-shadow" data-testid={`geofence-card-${fence.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#0052FF]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{fence.location_name}</p>
                    <p className="text-xs text-slate-500">{fence.address || 'No address'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-geofence-${fence.id}`} onClick={() => handleEdit(fence)} className="p-1.5 text-slate-400 hover:text-[#0052FF]"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-geofence-${fence.id}`} onClick={() => handleDelete(fence.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 rounded-md p-2">
                  <p className="text-xs text-slate-500">Latitude</p>
                  <p className="text-sm font-mono font-medium text-slate-800">{fence.latitude}</p>
                </div>
                <div className="bg-slate-50 rounded-md p-2">
                  <p className="text-xs text-slate-500">Longitude</p>
                  <p className="text-sm font-mono font-medium text-slate-800">{fence.longitude}</p>
                </div>
                <div className="bg-slate-50 rounded-md p-2">
                  <p className="text-xs text-slate-500">Radius</p>
                  <p className="text-sm font-mono font-medium text-slate-800">{fence.radius_meters}m</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {fence.is_active ? (
                  <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="w-3 h-3" /> Active</span>
                ) : (
                  <span className="text-xs text-slate-400">Inactive</span>
                )}
                {fence.enforce_for_all && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">All Employees</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="geofence-form-modal">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b">
                <h2 className="text-lg font-semibold">{editingId ? 'Edit Geofence' : 'Add Office Location'}</h2>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Location Name *</label>
                  <input data-testid="geofence-name-input" value={form.location_name} onChange={e => setForm({...form, location_name: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Head Office" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Address</label>
                  <input data-testid="geofence-address-input" value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="123 Business Park, City" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Latitude *</label>
                    <input data-testid="geofence-lat-input" type="number" step="any" value={form.latitude} onChange={e => setForm({...form, latitude: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="19.0760" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Longitude *</label>
                    <input data-testid="geofence-lng-input" type="number" step="any" value={form.longitude} onChange={e => setForm({...form, longitude: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="72.8777" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Radius (meters)</label>
                  <input data-testid="geofence-radius-input" type="number" value={form.radius_meters} onChange={e => setForm({...form, radius_meters: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="200" />
                  <p className="text-xs text-slate-400 mt-1">Recommended: 100-500 meters</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="enforceAll" checked={form.enforce_for_all} data-testid="geofence-enforce-all"
                    onChange={e => setForm({...form, enforce_for_all: e.target.checked})} className="rounded border-slate-300" />
                  <label htmlFor="enforceAll" className="text-sm text-slate-700">Enforce for all employees</label>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t">
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button data-testid="save-geofence-btn" onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default GeofenceSetup;
